import React from "react";

import { useIsBot } from "./IsBotContext";

/**
 * This hook tracks whether an element is in the viewport.
 * Along with the node and an initial value, it takes in an optional options object containing the same options
 * as the IntersectionObserver constructor, which it relies upon internally.
 *
 * `onEntry` runs synchronously inside the observer callback, before the state
 * update, so anything it records is available to the render that first sees
 * this hook return true. Use it for data that can only be read at observation
 * time, such as scroll position.
 *
 * Original source: https://usehooks.com/useOnScreen/
 */
export default function useOnScreen(
  node?: Element | null,
  initialValue = false,
  options: IntersectionObserverInit = {},
  onEntry?: (entry: IntersectionObserverEntry) => void,
): boolean {
  const [isIntersecting, setIntersecting] = React.useState(initialValue);
  const isPrerenderRequest = useIsBot();

  // Kept out of the observer effect's deps so an inline `onEntry` doesn't
  // rebuild the observer every render; the ref keeps it from going stale.
  const onEntryRef = React.useRef(onEntry);
  React.useEffect(() => {
    onEntryRef.current = onEntry;
  }, [onEntry]);

  React.useEffect(() => {
    if (!node) {
      return;
    }

    const handleEntry = (entry: IntersectionObserverEntry) => {
      onEntryRef.current?.(entry);
      setIntersecting(entry.isIntersecting);
    };

    const observer = new IntersectionObserver(
      ([entry]) => handleEntry(entry),
      options,
    );

    observer.observe(node);

    // Check if initially intersecting (fixes race condition for elements already in viewport)
    const entries = observer.takeRecords();
    if (entries.length > 0) {
      handleEntry(entries[0]);
    }

    return () => {
      observer.unobserve(node);
    };

    // Use the values of the options object to determine when to re-run the effect.
    // Using the object itself could cause extraneous re-renders because it's a reference type.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node, ...Object.values(options)]);

  return isPrerenderRequest || isIntersecting;
}
